import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { applyUIVariantAttribute } from './apply'
import { UI_VARIANT_RESOLUTION_TIMEOUT_MS, type UIVariant, type UIVariantMember, type UIVariantResolution, type UIVariantService } from './types'
import { UIVariantContext, type UIVariantContextValue, type UIVariantStatus } from './context'

/**
 * Boot contract (PLAN-20260909 slice 2, shared with chat by PLAN-20260929
 * slice 1; agion/Agion#3210). The app passes the signed-in member and its own
 * service:
 * - the new interface renders from the first paint and the resolution runs in
 *   the background;
 * - only a valid server answer of `legacy` for this organisation renders the
 *   current interface;
 * - no member, or a failed, slow (UI_VARIANT_RESOLUTION_TIMEOUT_MS), 401, 404
 *   or malformed answer keeps the new interface. In a locked environment that
 *   means a failed answer renders the new interface, an accepted gap
 *   (challenge 2026-10-18-ui-variant-switch-retirement): every write is still
 *   checked by the server.
 * Nothing in the browser (address, storage) chooses the interface.
 */
function isResolution(value: unknown): value is UIVariantResolution {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<UIVariantResolution>
  return (candidate.effectiveVariant === 'legacy' || candidate.effectiveVariant === 'next') && typeof candidate.canSwitch === 'boolean'
}

export interface UIVariantProviderProps {
  children: ReactNode
  user: UIVariantMember | null | undefined
  service: UIVariantService
}

export function UIVariantProvider({ children, user, service }: UIVariantProviderProps) {
  const organizationId = user?.organizationId
  const userId = user?.id
  // The service object may be recreated on every render; the call below reads
  // the latest one without re-running the boot effect.
  const serviceRef = useRef(service)
  useEffect(() => {
    serviceRef.current = service
  }, [service])
  const [status, setStatus] = useState<UIVariantStatus>('resolving')
  const [resolution, setResolution] = useState<UIVariantResolution | null>(null)
  const [variant, setVariant] = useState<UIVariant>('next')
  const generation = useRef(0)

  // The page is scoped to the new interface from the first paint (index.html
  // carries the same attribute); only a legacy answer removes it.
  useEffect(() => {
    applyUIVariantAttribute('next')
  }, [])

  useEffect(() => {
    if (!organizationId || !userId) {
      setStatus('failed')
      setVariant('next')
      applyUIVariantAttribute('next')
      return
    }
    const current = ++generation.current
    const controller = new AbortController()
    let settled = false
    const settle = (next: UIVariant, answer: UIVariantResolution | null, nextStatus: UIVariantStatus) => {
      if (settled || current !== generation.current) return
      settled = true
      setResolution(answer)
      setVariant(next)
      setStatus(nextStatus)
      applyUIVariantAttribute(next)
    }
    const timer = window.setTimeout(() => {
      controller.abort()
      settle('next', null, 'failed')
    }, UI_VARIANT_RESOLUTION_TIMEOUT_MS)
    serviceRef.current.getResolution(controller.signal)
      .then((answer) => {
        window.clearTimeout(timer)
        if (!isResolution(answer) || answer.organizationId !== organizationId) {
          settle('next', null, 'failed')
          return
        }
        settle(answer.effectiveVariant, answer, 'resolved')
      })
      .catch(() => {
        window.clearTimeout(timer)
        settle('next', null, 'failed')
      })
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [organizationId, userId])

  const value = useMemo<UIVariantContextValue>(() => ({ variant, status, resolution }), [variant, status, resolution])

  return <UIVariantContext.Provider value={value}>{children}</UIVariantContext.Provider>
}
